// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import { InferResult, RawBuilder, sql } from "kysely";

import { queryBuilder } from "./db";
import {
  getQueryBuilderForVersion,
  semverParseGte,
  VersionMap,
} from "./versioning";

export { semverParseGte };

export function getOwners() {
  return queryBuilder.selectFrom("mz_roles as r").select((eb) => [
    "r.id",
    "r.name",
    eb
      .or([
        sql<boolean>`(${hasSuperUserPrivileges()})`,
        eb.fn<boolean>("has_role", [
          sql.id("current_user"),
          "r.oid",
          sql.lit("USAGE"),
        ]),
      ])
      .$castTo<boolean>()
      .as("isOwner"),
  ]);
}

// Note(SangJunBak): This is a helper function to convert an expression to a JSON array.
// We need to explicitly type the output and cannot generically use the type of the
// input expression. This is because the expression becomes converted into a jsonb and we cannot
// implicitly convert the jsonb back to its original types on runtime.
export function jsonArrayFrom<T, R = unknown>(expr: R): RawBuilder<T[]> {
  return sql`(select coalesce(jsonb_agg(agg), '[]') from ${expr} as agg)`;
}

type ClusterReplicaUtilizationArgs = { clusterId: string };

/**
 * The most recent utilization sample per replica of a cluster, from the same
 * view the utilization charts read. Filtered on `cluster_id`, its index key.
 *
 * NOTE: scaled to 0-100; the view reports fractions.
 */
const latestClusterReplicaUtilizationQuery = ({
  clusterId,
}: ClusterReplicaUtilizationArgs) =>
  queryBuilder
    .selectFrom("mz_console_cluster_utilization_overview_3h as cru")
    .distinctOn("cru.replica_id")
    .where("cru.cluster_id", "=", clusterId)
    .select([
      "cru.replica_id",
      sql<number | null>`cru.cpu_percent * 100`.as("cpu_percent"),
      sql<number | null>`cru.memory_percent * 100`.as("memory_percent"),
      sql<number | null>`cru.disk_percent * 100`.as("disk_percent"),
      sql<number | null>`cru.heap_percent * 100`.as("heap_percent"),
    ])
    .orderBy("cru.replica_id")
    .orderBy("cru.occurred_at", "desc");

/**
 * The same reading from `mz_cluster_replica_utilization`, for environments
 * without the indexed view. Unwindowed and unindexed, so it disagrees with the
 * charts and costs a join per request; it keeps these surfaces working
 * mid-rollout.
 *
 * NOTE: the floor is 0.161.0, where `heap_percent` landed on this relation. No
 * release pairs a console with an environmentd older than that.
 */
const unindexedClusterReplicaUtilizationQuery = (
  _args: ClusterReplicaUtilizationArgs,
) =>
  queryBuilder
    .selectFrom("mz_cluster_replica_utilization as cru")
    .groupBy("replica_id")
    .select([
      "replica_id",
      sql<number | null>`SUM(cru.cpu_percent) / COUNT(process_id)`.as(
        "cpu_percent",
      ),
      // Processes of a replica share a size, so the mean of their per-process
      // ratios is the replica's share of its total allocation. An offline
      // process contributes a null that SUM skips and COUNT still counts, which
      // is what makes it read as idle rather than absent.
      sql<number | null>`SUM(cru.memory_percent) / COUNT(process_id)`.as(
        "memory_percent",
      ),
      sql<number | null>`SUM(cru.disk_percent) / COUNT(process_id)`.as(
        "disk_percent",
      ),
      sql<number | null>`MAX(cru.heap_percent)`.as("heap_percent"),
    ]);

const CLUSTER_REPLICA_UTILIZATION_QUERIES: VersionMap<
  ClusterReplicaUtilizationArgs,
  InferResult<ReturnType<typeof latestClusterReplicaUtilizationQuery>>[0]
> = {
  // The indexed view the charts read; see useReplicaUtilizationHistory, which
  // gates on the same version.
  // TODO: collapse to the indexed query once all environments are >= 26.32.
  "26.32.0": latestClusterReplicaUtilizationQuery,
  "0.0.0": unindexedClusterReplicaUtilizationQuery,
};

export function buildLatestClusterReplicaUtilizationTable(
  clusterId: string,
  environmentVersion?: string,
) {
  return getQueryBuilderForVersion(
    environmentVersion,
    CLUSTER_REPLICA_UTILIZATION_QUERIES,
  )({ clusterId });
}

/**
 * Useful since Kysely's count function returns a union of all number types in TypeScript
 */
export function countAll() {
  return sql<bigint>`count(*)`.as("count");
}

/**
 * This is important for flexible deployment mode where users aren't superusers by default
 * but need superuser privileges. RBAC is disabled by default in flexible deployment mode
 * so we can identify flexible deployment mode by checking the system variable `enable_rbac_checks`
 */
export function hasSuperUserPrivileges() {
  return sql.raw<boolean>(
    `SELECT mz_is_superuser() OR current_setting('enable_rbac_checks') = 'off'`,
  );
}

/**
 *
 * Represents Postgres and MySQL tables for pre and post source versioning. Also represents Kafka and Webhook sources post source versioning.
 */
export function buildSourceDependenciesQuery(sourceId: string) {
  return (
    queryBuilder
      // We use mz_object_dependencies instead of mz_tables and mz_sources since mz_tables is not a retained metrics object yet (https://github.com/MaterializeInc/materialize/pull/30788).
      .selectFrom("mz_object_dependencies as od")
      .leftJoin("mz_sources as subsources", "object_id", "subsources.id")
      .where("referenced_object_id", "=", sourceId)
      .where((eb) =>
        eb.or([
          eb("subsources.type", "<>", "progress"),
          // When the source dependency is a table, subsources.type is null.
          eb("subsources.type", "is", null),
        ]),
      )
      .select(["od.object_id as id", "od.referenced_object_id as sourceId"])
  );
}

/**
 * Each subsource and source-fed table, with the source that ingests it.
 * Subsources name their source through a dependency, and tables through
 * `mz_tables.source_id`.
 */
export function buildSourceExportsQuery() {
  return queryBuilder
    .selectFrom("mz_object_dependencies as od")
    .innerJoin("mz_sources as sub", "sub.id", "od.object_id")
    .innerJoin("mz_sources as parent", "parent.id", "od.referenced_object_id")
    .where("sub.type", "=", "subsource")
    .select(["od.object_id as exportId", "parent.id as sourceId"])
    .unionAll(
      queryBuilder
        .selectFrom("mz_tables")
        .where("source_id", "is not", null)
        .select(["id as exportId", sql<string>`source_id`.as("sourceId")]),
    );
}

const FIRST_REPLICA_SOURCE_STATISTICS_QUERIES = {
  // This is the query for modern versions of Materialize (>= v0.148.0).
  "0.148.0": () =>
    queryBuilder
      .with("latest_replica_per_source", (qb) =>
        qb
          .selectFrom("mz_cluster_replica_history as crh")
          // Filter to only extant cluster replicas - mz_cluster_replica_history contains
          // records for dropped replicas, but we only want statistics from active ones
          .innerJoin("mz_cluster_replicas as cr", "cr.id", "crh.replica_id")
          .innerJoin("mz_sources as s", "s.cluster_id", "cr.cluster_id")
          .select(["s.id as source_id", "crh.replica_id", "crh.created_at"])
          .distinctOn("s.id")
          .orderBy("s.id", "asc")
          .orderBy("crh.created_at", sql`desc NULLS LAST`),
      )
      .selectFrom("mz_source_statistics as stat")
      .innerJoin("latest_replica_per_source as lr", "lr.source_id", "stat.id")
      .whereRef("lr.replica_id", "=", "stat.replica_id")
      .selectAll("stat"),

  // The '0.0.0' query is a fallback for any version older than the ones specified above.
  "0.0.0": () =>
    queryBuilder
      .selectFrom("mz_source_statistics as stat")
      .distinctOn("stat.id")
      .selectAll("stat")
      .orderBy("stat.id"),
};

/**
 * Builds a subquery that selects the first replica's source statistics for each source.
 * This is useful for avoiding duplicate rows when joining with mz_source_statistics
 * in queries that don't need per-replica granularity.
 *
 * @param environmentVersion - The environment version string (e.g., "0.148.0").
 * If not provided, defaults to the latest query variant.
 */
export function buildFirstReplicaSourceStatisticsTable(
  environmentVersion?: string,
) {
  const queryBuilderFactory = getQueryBuilderForVersion(
    environmentVersion,
    FIRST_REPLICA_SOURCE_STATISTICS_QUERIES,
  );
  return queryBuilderFactory({});
}

export type FirstReplicaSourceStatistics = InferResult<
  ReturnType<typeof buildFirstReplicaSourceStatisticsTable>
>[0];

/**
 * Builds a subquery that aggregates heap metrics for each cluster replica.
 * This eliminates the need for groupBy in queries that only need heap metrics,
 * making the queries more readable and maintainable.
 */
export function buildClusterReplicaHeapMetricsTable() {
  return queryBuilder
    .selectFrom("mz_cluster_replica_metrics as crm")
    .groupBy("crm.replica_id")
    .select([
      "crm.replica_id",
      sql<bigint | null>`MAX(crm.heap_limit)`.as("heap_limit"),
      sql<bigint | null>`MAX(crm.heap_bytes)`.as("heap_bytes"),
    ]);
}

export type ClusterReplicaHeapMetrics = InferResult<
  ReturnType<typeof buildClusterReplicaHeapMetricsTable>
>[0];

/**
 * Builds a subquery over `mz_hydration_statuses` restricted to the objects the
 * console reports on, one row per (object, replica).
 *
 * Subsources and progress collections are excluded. The UI hides them, and each
 * source carries a progress collection and often several subsources, so they
 * multiply the row count several times over. Subsources have hydration rows of
 * their own because they run on their parent's cluster, which is why the
 * exclusion has to happen here rather than wherever a caller joins to
 * `mz_sources`.
 *
 * Callers add their own aggregation. The Maintained Objects feed groups by
 * `object_id` to count replicas per object; the cluster list groups by
 * `replica_id` to count objects per replica.
 */
export function buildHydrationStatusesTable() {
  return queryBuilder
    .selectFrom("mz_hydration_statuses as hs")
    .leftJoin("mz_sources as s", "s.id", "hs.object_id")
    .where((eb) =>
      eb.or([
        eb("s.type", "is", null),
        eb("s.type", "not in", ["subsource", "progress"]),
      ]),
    )
    .select(["hs.object_id", "hs.replica_id", "hs.hydrated"]);
}

export type HydrationStatus = InferResult<
  ReturnType<typeof buildHydrationStatusesTable>
>[0];

/**
 * The given ids as a relation `ids(id)`, to join an indexed relation against
 * in place of `id IN (...)`. Past 330 entries a literal list stops planning as
 * an index lookup and scans the whole index, testing every id on every row.
 * Joined to a relation indexed on the id alone, the list reads it by key at any
 * size (EXPLAIN on v26.48 at 2000 ids).
 */
export function buildIdListQuery(ids: string[]) {
  // A repeated id would repeat its rows, which doubles a caller's GROUP BY counts.
  const uniqueIds = [...new Set(ids)];
  return queryBuilder.selectNoFrom(
    sql<string>`unnest(ARRAY[${sql.join(uniqueIds)}]::text[])`.as("id"),
  );
}
